// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Writing `bundle.md` — the thing you paste into the chat window.
 *
 * THE FORMAT IS CHOSEN FOR ROUND-TRIP-ABILITY, AND FOR NOTHING ELSE.
 *
 * There is a widespread belief that XML-tagged bundles are better understood by
 * models than fenced ones. No measurement supporting it exists: the tool that
 * popularised the XML format justifies it with a general quote from a prompting
 * guide, and the next one claims "optimised output format" with no example and
 * no study. So unfence makes no comprehension claim, in the code or in the copy.
 *
 * What IS defensible is that a bundle emitting bare fences inherits this tool's
 * own inbound ambiguity. So every file is announced twice, in the two
 * conventions that measured best on real replies:
 *
 *     **src/app.ts**
 *     ```ts
 *     // filepath: src/app.ts
 *     …
 *     ```
 *
 * — a bold label immediately before the fence, which measured effectively 100%
 * precise when read back, and a `filepath:` first line, which is GitHub Copilot
 * Chat's own mandated convention and therefore the one a current model is most
 * likely to preserve. If the reply keeps either, unfence places the file with no
 * assignment step at all.
 *
 * FENCE ESCAPING IS NOT OPTIONAL. A source file containing three backticks —
 * every README in the world, and this very file — wrapped in a three-backtick
 * fence splits into two blocks and corrupts its own content. The fence length is
 * one longer than the longest run inside.
 */

import type { SourceFile } from '../types';

export interface BundleOptions {
  readonly folderName: string;
  /** Files omitted for any reason, so the model is told what it cannot see. */
  readonly omitted: readonly { path: string; reason: string }[];
  readonly generatedNote?: string;
}

/** The opening fence for content, long enough that the content cannot escape it. */
export function fenceFor(content: string): string {
  let longest = 0;
  for (const m of content.matchAll(/^\s*(`{3,})/gm)) longest = Math.max(longest, m[1].length);
  for (const m of content.matchAll(/(`{3,})/g)) longest = Math.max(longest, m[1].length);
  return '`'.repeat(Math.max(3, longest + 1));
}

/** A rough language hint for the fence. Display only; never read back as a path. */
export function languageFor(path: string): string {
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
  const map: Record<string, string> = {
    ts: 'ts', tsx: 'tsx', mts: 'ts', cts: 'ts', js: 'js', jsx: 'jsx', mjs: 'js', cjs: 'js',
    json: 'json', jsonc: 'jsonc', html: 'html', htm: 'html', css: 'css', scss: 'scss',
    less: 'less', md: 'md', mdx: 'mdx', py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
    java: 'java', kt: 'kotlin', swift: 'swift', php: 'php', cs: 'csharp', c: 'c', h: 'c',
    cpp: 'cpp', hpp: 'cpp', cc: 'cpp', sql: 'sql', yaml: 'yaml', yml: 'yaml', toml: 'toml',
    xml: 'xml', svg: 'xml', sh: 'bash', bash: 'bash', zsh: 'bash', vue: 'vue',
    svelte: 'svelte', astro: 'astro', dart: 'dart', lua: 'lua', r: 'r', jl: 'julia',
  };
  return map[ext] ?? '';
}

/** The comment syntax that will not break the file it is prepended to. */
function commentFor(path: string): [string, string] {
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
  if (['py', 'rb', 'sh', 'bash', 'zsh', 'yaml', 'yml', 'toml', 'r', 'pl', 'jl', 'ex', 'exs'].includes(ext)) return ['# ', ''];
  if (['html', 'htm', 'xml', 'svg', 'md', 'mdx', 'vue', 'svelte', 'astro'].includes(ext)) return ['<!-- ', ' -->'];
  if (['css', 'scss', 'less'].includes(ext)) return ['/* ', ' */'];
  if (['json', 'jsonc', 'json5'].includes(ext)) return ['', '']; // JSON has no comments
  return ['// ', ''];
}

/**
 * A directory tree of what went in.
 *
 * Retained because it is the only part of the bundle that tells the model what
 * was left OUT — without it, a model reasons confidently about a project it has
 * been shown two-thirds of.
 */
export function treeOf(paths: readonly string[]): string {
  const lines: string[] = [];
  const sorted = [...paths].sort();
  let previous: string[] = [];
  for (const path of sorted) {
    const parts = path.split('/');
    for (let i = 0; i < parts.length - 1; i++) {
      if (previous[i] === parts[i]) continue;
      lines.push(`${'  '.repeat(i)}${parts[i]}/`);
      previous = parts.slice(0, i + 1);
    }
    lines.push(`${'  '.repeat(parts.length - 1)}${parts[parts.length - 1]}`);
    previous = parts;
  }
  return lines.join('\n');
}

/**
 * Build the bundle as an array of parts.
 *
 * Parts rather than one joined string so the caller can hand them straight to a
 * `Blob`: peak memory during assembly is measured at 1.18–1.30× the source
 * bytes, and on a phone that multiplier is the difference between a bundle and a
 * tab the operating system kills with no catchable exception.
 */
export function buildBundle(files: readonly SourceFile[], options: BundleOptions): string[] {
  const included = files.filter((f) => !f.ignored && !f.refused && f.text !== null);
  const parts: string[] = [];

  parts.push(
    `# ${options.folderName}\n\n`,
    'This is a snapshot of a project folder. Each file is announced by a bold path line and repeats\n' +
      'its path in a `filepath:` comment on the first line of the block.\n\n' +
      '**When you return a file, keep the `filepath:` line and the bold path line, and return the\n' +
      'complete file.** Do not abbreviate the middle with `// ... rest unchanged` — a tool applies\n' +
      'these blocks directly to the folder and will refuse a file that has been shortened that way.\n\n',
  );

  if (options.omitted.length) {
    parts.push(
      `## Not included (${options.omitted.length} ${options.omitted.length === 1 ? 'file' : 'files'})\n\n`,
      'These exist in the project but were left out, so do not assume they are absent:\n\n',
    );
    const shown = options.omitted.slice(0, 60);
    for (const o of shown) parts.push(`- \`${o.path}\` — ${o.reason}\n`);
    if (options.omitted.length > shown.length) {
      parts.push(`- …and ${options.omitted.length - shown.length} more\n`);
    }
    parts.push('\n');
  }

  parts.push('## Files\n\n```\n', treeOf(included.map((f) => f.path)), '\n```\n\n');

  for (const file of included) {
    const fence = fenceFor(file.text ?? '');
    const [open, close] = commentFor(file.path);
    const marker = open ? `${open}filepath: ${file.path}${close}\n` : '';
    const body = file.text ?? '';
    parts.push(
      `**${file.path}**\n\n`,
      `${fence}${languageFor(file.path)}\n`,
      marker,
      body.endsWith('\n') || body === '' ? body : `${body}\n`,
      `${fence}\n\n`,
    );
  }

  if (options.generatedNote) parts.push(`---\n\n${options.generatedNote}\n`);
  return parts;
}

/**
 * `skipped.md` — the audit trail.
 *
 * Every block that did not become a file, with the reason. A tool that silently
 * drops half its input looks exactly like one that found nothing, and this is
 * the file that makes the difference visible.
 */
export interface SkippedEntry {
  readonly label: string;
  readonly reason: string;
  readonly detail?: string;
}

export function buildSkipped(entries: readonly SkippedEntry[], summary: string): string {
  const parts = [
    '# Skipped\n\n',
    `${summary}\n\n`,
    'Nothing listed here was written to your folder or included in the patch.\n\n',
  ];
  if (entries.length === 0) parts.push('Nothing was skipped.\n');
  for (const e of entries) {
    parts.push(`## ${e.label}\n\n`, `${e.reason}\n\n`);
    if (e.detail) parts.push('```\n', e.detail.slice(0, 4000), '\n```\n\n');
  }
  parts.push(
    '---\n\n',
    'unfence never generates a binary patch: content arrives from a chat reply as text, and a\n' +
      'binary file cannot. If one of these needed to be binary, it has to be copied by hand.\n',
  );
  return parts.join('');
}
